import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, CircularProgress, Snackbar, Alert, AppBar, Toolbar, IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth, storage } from '../firebase';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import koLocale from 'date-fns/locale/ko';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import CropModal from './CropModal';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import PushPinIcon from '@mui/icons-material/PushPin';

const statusOptions = [
  { value: 'not_started', label: '읽기 전' },
  { value: 'reading', label: '읽는 중' },
  { value: 'completed', label: '완독' },
];

function BookMemoEdit() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [book, setBook] = useState(null);
  const [memo, setMemo] = useState('');
  const [readStatus, setReadStatus] = useState('not_started');
  const [dateRange, setDateRange] = useState([null, null]);
  const [readingPeriods, setReadingPeriods] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [starRating, setStarRating] = useState(0);
  const [recommendation, setRecommendation] = useState('');
  const [mustRead, setMustRead] = useState('');
  const [emotions, setEmotions] = useState([]);
  const [genres, setGenres] = useState([]);
  const [userThumbnail, setUserThumbnail] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [isPinned, setIsPinned] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  // 섹션별 ref 생성
  const readStatusRef = useRef(null);
  const pinRef = useRef(null);
  const starRatingRef = useRef(null);
  const recommendationRef = useRef(null);
  const emotionsRef = useRef(null);
  const genresRef = useRef(null);
  const mustReadRef = useRef(null);

  useEffect(() => {
    const fetchBook = async () => {
      setLoading(true);
      try {
        const bookRef = doc(db, 'books', bookId);
        const docSnap = await getDoc(bookRef);
        if (!docSnap.exists()) throw new Error('책 정보를 찾을 수 없습니다.');
        const data = docSnap.data();
        // 권한 체크
        if (!auth.currentUser || data.uid !== auth.currentUser.uid) {
          setSnackbar({ open: true, message: '접근 권한이 없습니다.', severity: 'error' });
          setTimeout(() => navigate(-1), 1200);
          return;
        }
        setBook({ id: docSnap.id, ...data });
        setMemo(data.memo || '');
        setReadStatus(data.readStatus || 'not_started');
        setDateRange([
          data.startedAt ? (data.startedAt.toDate ? data.startedAt.toDate() : new Date(data.startedAt)) : new Date(),
          data.finishedAt ? (data.finishedAt.toDate ? data.finishedAt.toDate() : new Date(data.finishedAt)) : null
        ]);
        
        // 다중 읽은 기간 데이터 로드 (기존 데이터와 호환성 유지)
        if (data.readingPeriods && Array.isArray(data.readingPeriods) && data.readingPeriods.length > 0) {
          setReadingPeriods(data.readingPeriods.map(period => ({
            startDate: period.startDate ? (period.startDate.toDate ? period.startDate.toDate() : new Date(period.startDate)) : null,
            endDate: period.endDate ? (period.endDate.toDate ? period.endDate.toDate() : new Date(period.endDate)) : null
          })));
        } else {
          // 기존 데이터가 있으면 첫 번째 기간으로 설정
          if (data.startedAt) {
            setReadingPeriods([{
              startDate: data.startedAt.toDate ? data.startedAt.toDate() : new Date(data.startedAt),
              endDate: data.finishedAt ? (data.finishedAt.toDate ? data.finishedAt.toDate() : new Date(data.finishedAt)) : null
            }]);
          } else {
            // 기본 기간 설정
            setReadingPeriods([{ startDate: null, endDate: null }]);
          }
        }
        setStarRating(data.starRating || 0);
        setRecommendation(data.recommendation || '');
        // 독서 목적 & 기대 경험 (새 필드명 우선, 기존 필드 fallback)
        setMustRead(
          Array.isArray(data.readingPurpose) ? data.readingPurpose : 
          (data.readingPurpose ? [data.readingPurpose] : 
          (Array.isArray(data.mustRead) ? data.mustRead : 
          (data.mustRead ? [data.mustRead] : [])))
        );
        setEmotions(data.emotions || []);
        // 소장 여부 (새 필드명 우선, 기존 genres 필드 fallback)
        setGenres(data.ownershipStatus || data.genres || []);
        setUserThumbnail(data.userThumbnail || '');
        setIsPinned(data.isPinned || false);
      } catch (e) {
        setSnackbar({ open: true, message: e.message, severity: 'error' });
        setTimeout(() => navigate(-1), 1200);
      } finally {
        setLoading(false);
      }
    };
    fetchBook();
  }, [bookId, navigate]);

  const handleDateRangeChange = (newValue) => {
    setDateRange(newValue);
    // 첫 번째 기간도 동기화
    if (readingPeriods.length > 0) {
      updateReadingPeriodRange(0, newValue[0], newValue[1]);
    }
  };

  // 읽은 기간 추가 함수
  const addReadingPeriod = () => {
    setReadingPeriods([...readingPeriods, { startDate: null, endDate: null }]);
  };

  // 읽은 기간 삭제 함수
  const removeReadingPeriod = (index) => {
    if (readingPeriods.length > 1) {
      const newPeriods = readingPeriods.filter((_, i) => i !== index);
      setReadingPeriods(newPeriods);
    }
  };

  // 읽은 기간 업데이트 함수
  const updateReadingPeriod = (index, field, value) => {
    const newPeriods = [...readingPeriods];
    newPeriods[index] = { ...newPeriods[index], [field]: value };
    setReadingPeriods(newPeriods);
  };

  // 읽은 기간 범위 업데이트 함수 (DateRangePicker용)
  const updateReadingPeriodRange = (index, startDate, endDate) => {
    const newPeriods = [...readingPeriods];
    newPeriods[index] = { 
      ...newPeriods[index], 
      startDate: startDate,
      endDate: endDate
    };
    setReadingPeriods(newPeriods);
  };

  // 스크롤 함수
  const scrollToSection = useCallback((ref, offset = 80) => {
    if (!ref?.current || isScrolling) return;
    
    setIsScrolling(true);
    
    const element = ref.current;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
    
    // 스크롤 완료 후 상태 초기화
    setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  }, [isScrolling]);

  const handleSave = async () => {
    if (!book) return;
    if (readStatus === 'completed' && dateRange[0] && dateRange[1] && dateRange[0] > dateRange[1]) {
      setSnackbar({ open: true, message: '완독일은 시작일 이후여야 합니다.', severity: 'warning' });
      return;
    }
    
    // 고정 설정 시 최대 3개 제한 체크
    if (isPinned && !book.isPinned) {
      try {
        const booksRef = collection(db, 'books');
        const pinnedBooksQuery = query(
          booksRef,
          where('uid', '==', auth.currentUser.uid),
          where('isPinned', '==', true)
        );
        const pinnedBooksSnapshot = await getDocs(pinnedBooksQuery);
        if (pinnedBooksSnapshot.size >= 3) {
          setSnackbar({ 
            open: true, 
            message: '최대 3개까지만 상단 고정할 수 있어요. 다른 책의 고정을 해제해주세요.', 
            severity: 'warning' 
          });
          return;
        }
      } catch (error) {
        console.error('고정된 책 개수 확인 중 오류:', error);
        setSnackbar({ 
          open: true, 
          message: '고정 설정 확인 중 오류가 발생했습니다.', 
          severity: 'error' 
        });
        return;
      }
    }
    
    setSaving(true);
    try {
      const bookRef = doc(db, 'books', book.id);
      await updateDoc(bookRef, {
        memo,
        readStatus,
        startedAt: dateRange[0] ? new Date(dateRange[0]) : null,
        finishedAt: readStatus === 'completed' && dateRange[1] ? new Date(dateRange[1]) : null,
        // 다중 읽은 기간 데이터 저장
        readingPeriods: readingPeriods.map(period => ({
          startDate: period.startDate ? new Date(period.startDate) : null,
          endDate: period.endDate ? new Date(period.endDate) : null
        })),
        starRating,
        recommendation,
        readingPurpose: Array.isArray(mustRead) ? mustRead : (mustRead ? [mustRead] : []), // 독서 목적 & 기대 경험
        emotions, // 감정 & 느낌
        ownershipStatus: genres, // 소장 여부 (기존 genres 필드 재활용)
        isPinned,
        pinnedAt: isPinned ? new Date() : null,
      });
      setSnackbar({ open: true, message: '저장되었습니다.', severity: 'success' });
      setTimeout(() => navigate(`/book/${book.id}`), 800);
    } catch (e) {
      setSnackbar({ open: true, message: '저장 실패: ' + e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // 이미지 업로드 input 핸들러 (크롭 모달용)
  const handleImageInput = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    setPendingImageFile(file);
    setCropModalOpen(true);
  };

  // 크롭 완료 시 업로드
  const handleCropComplete = async (croppedBlob) => {
    setCropModalOpen(false);
    setPendingImageFile(null);
    if (!croppedBlob) return;
    const croppedFile = new File([croppedBlob], 'cropped.jpg', { type: 'image/jpeg' });
    // 기존 handleImageChange의 업로드 부분만 재사용
    setUploading(true);
    setUploadError('');
    try {
      if (userThumbnail) {
        const prevRef = ref(storage, userThumbnail);
        try { await deleteObject(prevRef); } catch {}
      }
      const storageRef = ref(storage, `books/${bookId}/userThumbnail_${Date.now()}`);
      await uploadBytes(storageRef, croppedFile);
      const url = await getDownloadURL(storageRef);
      setUserThumbnail(url);
      await updateDoc(doc(db, 'books', bookId), { userThumbnail: url });
    } catch (err) {
      setUploadError('이미지 업로드 실패: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // 이미지 삭제 핸들러
  const handleDeleteImage = async () => {
    if (!userThumbnail) return;
    setUploading(true);
    setUploadError('');
    try {
      const storageRef = ref(storage, userThumbnail);
      await deleteObject(storageRef);
      setUserThumbnail('');
      await updateDoc(doc(db, 'books', bookId), { userThumbnail: '' });
    } catch (err) {
      setUploadError('이미지 삭제 실패: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff', pb: 10 }}>
      {/* 상단 고정 헤더 */}
      <AppBar position="fixed" color="default" elevation={0} sx={{ borderBottom: '1px solid #eee', bgcolor: '#fff' }}>
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton edge="start" onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', ml: 1 }}>책 리뷰</Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      
      {/* 통일된 컨테이너 시작 */}
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 2, px: 2 }}>
        
        {/* 책 커버 사진 섹션 */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '1.05rem', color: '#333' }}>
            📷 책 커버 사진
          </Typography>
          <Box sx={{ 
            p: 2.5,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 3,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#f1f3f4',
              borderColor: '#dee2e6',
            }
          }}>
            <Typography sx={{ 
              fontSize: '0.9rem', 
              fontWeight: 500, 
              color: '#6c757d',
              mb: 2
            }}>
            직접 찍은 책의 모습을 기록할 수 있어요
          </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 140 }}>
            {userThumbnail ? (
                <Box sx={{ position: 'relative', width: 100, height: 140, borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  <img src={userThumbnail} alt="내가 등록한 책 이미지" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <IconButton
                  size="small"
                  onClick={handleDeleteImage}
                  aria-label="이미지 삭제"
                    sx={{ 
                      position: 'absolute', 
                      top: 8, 
                      right: 8, 
                      bgcolor: 'rgba(255,255,255,0.9)', 
                      color: '#495057', 
                      borderRadius: '50%', 
                      p: 0.5, 
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      backdropFilter: 'blur(4px)',
                      '&:hover': { 
                        bgcolor: 'rgba(255,255,255,1)',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                sx={{
                    width: 100, 
                    height: 140, 
                    borderRadius: 3, 
                    border: '2px dashed #dee2e6',
                    color: '#6c757d', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '0.85rem', 
                    fontWeight: 500, 
                    bgcolor: '#fff', 
                    cursor: 'pointer', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      bgcolor: '#f8f9fa', 
                      borderColor: '#1a237e',
                      color: '#1a237e',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                }}
                disabled={uploading}
              >
                  <AddAPhotoIcon sx={{ fontSize: 28, mb: 1 }} />
                이미지 등록
                <input type="file" accept="image/*" hidden onChange={handleImageInput} />
              </Button>
            )}
          </Box>
            {uploadError && (
              <Typography 
                color="error" 
                sx={{ 
                  fontSize: '0.85rem', 
                  mt: 1.5, 
                  textAlign: 'center',
                  p: 1,
                  bgcolor: '#fff5f5',
                  borderRadius: 2,
                  border: '1px solid #ffebee'
                }}
              >
                {uploadError}
              </Typography>
            )}
        </Box>
        </Box>
        
        {/* 메모 섹션 */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '1.05rem', color: '#333' }}>
            ✍️ 메모
          </Typography>
          <Box sx={{ 
            p: 2.5,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 3,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#f1f3f4',
              borderColor: '#dee2e6',
            }
          }}>
          <TextField
            multiline
              minRows={4}
            maxRows={8}
            value={memo}
            onChange={e => setMemo(e.target.value)}
              placeholder="책에 대한 생각, 느낌, 인상깊은 구절이나 경험을 자유롭게 기록해보세요."
            fullWidth
              variant="outlined"
              inputProps={{ maxLength: 300 }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: 2,
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 16px rgba(26,35,126,0.15)',
                    borderColor: '#1a237e',
                  },
                  '& fieldset': {
                    border: '1px solid #e9ecef',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1a237e',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1a237e',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.95rem',
                  color: '#212529',
                  lineHeight: 1.6,
                  '&::placeholder': {
                    color: '#6c757d',
                    opacity: 1,
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  transition: 'all 0.2s ease',
                }
              }}
            />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 1.5,
              px: 0.5
            }}>
              <Typography sx={{ 
                fontSize: '0.8rem', 
                color: '#6c757d',
                fontWeight: 500
              }}>
                책에 대한 감상을 자유롭게 기록하기
              </Typography>
              <Typography sx={{ 
                fontSize: '0.85rem', 
                color: memo.length > 250 ? '#dc3545' : '#6c757d',
                fontWeight: 600,
                bgcolor: memo.length > 250 ? '#fff5f5' : 'transparent',
                px: memo.length > 250 ? 1 : 0,
                py: memo.length > 250 ? 0.5 : 0,
                borderRadius: memo.length > 250 ? 1 : 0,
                transition: 'all 0.2s ease'
              }}>
                {memo.length}/300
              </Typography>
        </Box>
          </Box>
        </Box>
        {/* 완독 여부 섹션 */}
        <Box ref={readStatusRef} sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '1.05rem', color: '#333' }}>
            📖 완독 여부
          </Typography>
          <Box sx={{ 
            p: 2.5,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 3,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#f1f3f4',
              borderColor: '#dee2e6',
            }
          }}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            {statusOptions.map(opt => (
              <Box
                key={opt.value}
                onClick={() => {
                  setReadStatus(opt.value);
                  // 읽는 중이나 완독 선택 시 해당 섹션으로 스크롤
                  if (opt.value === 'reading' || opt.value === 'completed') {
                    setTimeout(() => scrollToSection(readStatusRef), 100);
                  }
                }}
                sx={{
                  px: 2.5, py: 1.3,
                  borderRadius: 25,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  boxShadow: readStatus === opt.value ? '0 4px 16px rgba(26,35,126,0.25)' : '0 2px 8px rgba(0,0,0,0.06)',
                  bgcolor: readStatus === opt.value ? '#1a237e' : '#fff',
                  color: readStatus === opt.value ? '#fff' : '#495057',
                  border: readStatus === opt.value ? '2px solid #1a237e' : '1px solid #e9ecef',
                  minWidth: 80,
                  textAlign: 'center',
                  userSelect: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: readStatus === opt.value ? '#283593' : '#f8f9fa',
                    transform: 'translateY(-2px)',
                    boxShadow: readStatus === opt.value ? '0 6px 20px rgba(26,35,126,0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                    borderColor: readStatus === opt.value ? '#283593' : '#1a237e',
                  },
                  '&:active': {
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                {opt.label}
              </Box>
            ))}
          </Box>
          {(readStatus === 'reading' || readStatus === 'completed') && (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={koLocale}>
                <Box sx={{ mt: 2 }}>
                <DateRangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  localeText={{ start: '시작일', end: '완독일' }}
                  calendars={1}
                  disableFuture
                  minDate={new Date('1900-01-01')}
                  maxDate={new Date()}
                  showDaysOutsideCurrentMonth
                  renderInput={(startProps, endProps) => (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 1,
                      p: 2.5,
                      bgcolor: '#f8f9fa',
                      borderRadius: 3,
                      border: '1px solid #e9ecef',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: '#f1f3f4',
                        borderColor: '#dee2e6',
                      }
                    }}>
                      <Typography sx={{ 
                        fontSize: '0.9rem', 
                        fontWeight: 600, 
                        color: '#495057',
                        mb: 1
                      }}>
                        📅 읽은 기간
                      </Typography>
                      <TextField
                        {...startProps}
                        fullWidth
                        placeholder="기간을 선택하세요"
                        variant="outlined"
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#fff',
                            borderRadius: 2,
                            fontSize: '1rem',
                            fontWeight: 500,
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            },
                                                '&.Mui-focused': {
                      boxShadow: '0 4px 16px rgba(26,35,126,0.15)',
                      borderColor: '#1a237e',
                    },
                            '& fieldset': {
                              border: '1px solid #e9ecef',
                            },
                            '&:hover fieldset': {
                              borderColor: '#1a237e',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1a237e',
                              borderWidth: '2px',
                            },
                          },
                          '& .MuiInputBase-input': {
                            textAlign: 'center',
                            py: 1.5,
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            color: '#212529',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            transition: 'all 0.2s ease',
                          }
                        }}
                        inputProps={{
                          ...startProps.inputProps,
                          style: {
                            textAlign: 'center',
                            cursor: 'pointer',
                          }
                        }}
                        value={
                          dateRange[0] && dateRange[1]
                            ? `${dateRange[0].getFullYear()}.${String(dateRange[0].getMonth() + 1).padStart(2, '0')}.${String(dateRange[0].getDate()).padStart(2, '0')} ~ ${dateRange[1].getFullYear()}.${String(dateRange[1].getMonth() + 1).padStart(2, '0')}.${String(dateRange[1].getDate()).padStart(2, '0')}`
                            : dateRange[0] 
                              ? `${dateRange[0].getFullYear()}.${String(dateRange[0].getMonth() + 1).padStart(2, '0')}.${String(dateRange[0].getDate()).padStart(2, '0')} ~ 완독일 선택`
                              : '시작일과 완독일을 선택하세요'
                        }
                        onClick={startProps.inputProps.onClick}
                        readOnly
                      />
                    </Box>
                  )}
                />
              </Box>
              
              {/* 다중 읽은 기간 관리 */}
              <Box sx={{ mt: 2 }}>
                {/* 추가 기간 목록 */}
                {readingPeriods.slice(1).map((period, index) => (
                  <Box key={index + 1} sx={{ 
                    mb: 1.5,
                    p: 2,
                    bgcolor: '#fff',
                    borderRadius: 2,
                    border: '1px solid #e9ecef',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#f8f9fa',
                      borderColor: '#dee2e6',
                    }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography sx={{ 
                        fontSize: '0.8rem', 
                        fontWeight: 600, 
                        color: '#495057'
                      }}>
                        {index + 2}번째 기간
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => removeReadingPeriod(index + 1)}
                        sx={{
                          minWidth: 'auto',
                          px: 1,
                          py: 0.5,
                          fontSize: '0.7rem',
                          color: '#dc3545',
                          bgcolor: '#fff',
                          border: '1px solid #dc3545',
                          borderRadius: 1.5,
                          '&:hover': {
                            bgcolor: '#dc3545',
                            color: '#fff'
                          }
                        }}
                      >
                        삭제
                      </Button>
                    </Box>
                    
                    <DateRangePicker
                      value={[period.startDate, period.endDate]}
                      onChange={(newValue) => {
                        updateReadingPeriodRange(index + 1, newValue[0], newValue[1]);
                      }}
                      localeText={{ start: '시작일', end: '완독일' }}
                      calendars={1}
                      disableFuture
                      minDate={new Date('1900-01-01')}
                      maxDate={new Date()}
                      showDaysOutsideCurrentMonth
                      renderInput={(startProps, endProps) => (
                        <TextField
                          {...startProps}
                          fullWidth
                          placeholder="기간을 선택하세요"
                          variant="outlined"
                          sx={{ 
                            '& .MuiOutlinedInput-root': {
                              bgcolor: '#f8f9fa',
                              borderRadius: 2,
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              border: 'none',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                              '&:hover': {
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              },
                              '&.Mui-focused': {
                                boxShadow: '0 2px 12px rgba(26,35,126,0.15)',
                                borderColor: '#1a237e',
                              },
                              '& fieldset': {
                                border: '1px solid #e9ecef',
                              },
                              '&:hover fieldset': {
                                borderColor: '#1a237e',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#1a237e',
                                borderWidth: '2px',
                              },
                            },
                            '& .MuiInputBase-input': {
                              textAlign: 'center',
                              py: 1.2,
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              color: '#212529',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              transition: 'all 0.2s ease',
                            }
                          }}
                          inputProps={{
                            ...startProps.inputProps,
                            style: {
                              textAlign: 'center',
                              cursor: 'pointer',
                            }
                          }}
                          value={
                            period.startDate && period.endDate
                              ? `${period.startDate.getFullYear()}.${String(period.startDate.getMonth() + 1).padStart(2, '0')}.${String(period.startDate.getDate()).padStart(2, '0')} ~ ${period.endDate.getFullYear()}.${String(period.endDate.getMonth() + 1).padStart(2, '0')}.${String(period.endDate.getDate()).padStart(2, '0')}`
                              : period.startDate 
                                ? `${period.startDate.getFullYear()}.${String(period.startDate.getMonth() + 1).padStart(2, '0')}.${String(period.startDate.getDate()).padStart(2, '0')} ~ 완독일 선택`
                                : '시작일과 완독일을 선택하세요'
                          }
                          onClick={startProps.inputProps.onClick}
                          readOnly
                        />
                      )}
                    />
                  </Box>
                ))}
                
                {/* 읽은 기간 추가 버튼 */}
                <Button
                  onClick={addReadingPeriod}
                  variant="outlined"
                  fullWidth
                  sx={{
                    mt: 1,
                    py: 1.2,
                    border: '2px dashed #dee2e6',
                    color: '#6c757d',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    borderRadius: 2,
                    bgcolor: '#fff',
                    '&:hover': {
                      borderColor: '#1a237e',
                      color: '#1a237e',
                      bgcolor: '#f8f9fa'
                    }
                  }}
                >
                  + 읽은 기간 추가하기
                </Button>
              </Box>
            </LocalizationProvider>
          )}
          </Box>
        </Box>
        
        {/* 상단 고정 기능 */}
        <Box ref={pinRef} sx={{ mb: 4 }}>
          <Box sx={{ 
            p: 2.5,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 3,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#f1f3f4',
              borderColor: '#dee2e6',
            }
          }}>
          <Box
            onClick={() => {
              setIsPinned(!isPinned);
              // 고정 설정 시 해당 섹션으로 스크롤
              setTimeout(() => scrollToSection(pinRef), 100);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
                gap: 2,
              p: 2,
              borderRadius: 2,
              cursor: 'pointer',
                bgcolor: isPinned ? '#fff3cd' : '#fff',
                border: `1px solid ${isPinned ? '#ffc107' : '#e9ecef'}`,
                boxShadow: isPinned ? '0 4px 16px rgba(255,193,7,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
              transition: 'all 0.2s ease',
              '&:hover': {
                  bgcolor: isPinned ? '#fff2b3' : '#f8f9fa',
                  borderColor: isPinned ? '#ffb300' : '#1a237e',
                  transform: 'translateY(-2px)',
                  boxShadow: isPinned ? '0 6px 20px rgba(255,193,7,0.2)' : '0 4px 12px rgba(26,35,126,0.1)',
              },
              '&:active': {
                  transform: 'translateY(-1px)',
              },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: isPinned ? '#ffc107' : '#fff',
                border: `2px solid ${isPinned ? '#ffb300' : '#e9ecef'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <PushPinIcon 
                  sx={{
                  fontSize: '16px', 
                  color: isPinned ? '#fff' : '#6c757d',
                  transform: isPinned ? 'rotate(-45deg)' : 'rotate(0deg)',
                  transition: 'all 0.2s ease',
                }} 
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: isPinned ? '#856404' : '#495057',
                  fontSize: '1rem',
                  mb: 0.5,
                }}
              >
                {isPinned ? '상단에 고정됨' : '상단에 고정하기'}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  color: isPinned ? '#856404' : '#6c757d',
                  fontWeight: 500,
                }}
              >
                {isPinned ? '프로필 상단에 표시됩니다' : '최대 3개까지 고정 가능합니다'}
              </Typography>
            </Box>
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                bgcolor: isPinned ? '#ffc107' : 'transparent',
                border: isPinned ? 'none' : '1px solid #e9ecef',
                transition: 'all 0.2s ease',
              }}
            >
              <Typography
                sx={{ 
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: isPinned ? '#fff' : '#6c757d',
                }}
              >
                {isPinned ? 'ON' : 'OFF'}
              </Typography>
            </Box>
            </Box>
          </Box>
        </Box>
        
        {/* 별점 섹션 */}
        <Box ref={starRatingRef} sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '1.05rem', color: '#333' }}>
            ⭐ 별점
          </Typography>
          <Box sx={{ 
            p: 2.5,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 3,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#f1f3f4',
              borderColor: '#dee2e6',
            }
          }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[1,2,3,4,5].map((star) => (
                <Box
                  key={star}
                  onClick={() => {
                    setStarRating(starRating === star ? 0 : star);
                    // 별점 선택 시 해당 섹션으로 스크롤
                    setTimeout(() => scrollToSection(starRatingRef), 100);
                  }}
                  sx={{
                    px: 1.8, py: 1.2,
                    borderRadius: 12,
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    boxShadow: starRating === star ? '0 3px 12px 0 rgba(255,215,0,0.3)' : '0 1px 3px 0 rgba(0,0,0,0.1)',
                    bgcolor: starRating === star ? '#FFD700' : '#fff',
                    color: starRating === star ? '#333' : '#666',
                    border: starRating === star ? '2px solid #FFD700' : '1.5px solid #e0e0e0',
                    mr: 0.5,
                  mb: 0.5,
                    minWidth: 58,
                  textAlign: 'center',
                  userSelect: 'none',
                    transition: 'all 0.2s ease',
                  '&:hover': {
                      bgcolor: starRating === star ? '#FFC107' : '#f8f9fa',
                      transform: 'translateY(-2px)',
                      boxShadow: starRating === star ? '0 5px 16px 0 rgba(255,215,0,0.4)' : '0 3px 8px 0 rgba(0,0,0,0.15)',
                  },
                  '&:active': {
                      transform: 'translateY(-1px)',
                  },
                  }}
                >
                  {`★${star}`}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* 추천도 섹션 */}
        <Box ref={recommendationRef} sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '1.05rem', color: '#333' }}>
            👍 추천도
          </Typography>
          <Box sx={{ 
            p: 2.5,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 3,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#f1f3f4',
              borderColor: '#dee2e6',
            }
          }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { label: '💖 필독', value: '필독', color: '#1a237e' },
                { label: '👍 추천', value: '추천', color: '#283593' },
                { label: '🤷‍♀️ 보통', value: '보통', color: '#FFD700' },
                { label: '👎 비추천', value: '비추천', color: '#6c757d' },
              ].map((item) => (
                <Box
                  key={item.value}
                  onClick={() => {
                    setRecommendation(recommendation === item.value ? '' : item.value);
                    // 추천도 선택 시 해당 섹션으로 스크롤
                    setTimeout(() => scrollToSection(recommendationRef), 100);
                  }}
                  sx={{
                    px: 1.8, py: 1.1,
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    boxShadow: recommendation === item.value ? `0 3px 12px 0 ${item.color}30` : '0 1px 3px 0 rgba(0,0,0,0.1)',
                    bgcolor: recommendation === item.value ? item.color : '#fff',
                    color: recommendation === item.value ? '#fff' : '#666',
                    border: recommendation === item.value ? `2px solid ${item.color}` : '1.5px solid #e0e0e0',
                    mr: 0.5,
                    mb: 0.5,
                    minWidth: 72,
                    textAlign: 'center',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: recommendation === item.value ? item.color : '#f8f9fa',
                      transform: 'translateY(-2px)',
                      boxShadow: recommendation === item.value ? `0 5px 16px 0 ${item.color}40` : '0 3px 8px 0 rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
          
        {/* 감정 & 느낌 섹션 */}
        <Box ref={emotionsRef} sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '1.05rem', color: '#333' }}>
            💭 감정 & 느낌 <Typography component="span" sx={{ fontSize: '0.8rem', color: '#888', fontWeight: 400 }}>(최대 3개)</Typography>
          </Typography>
          <Box sx={{ 
            p: 2.5,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 3,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#f1f3f4',
              borderColor: '#dee2e6',
            }
          }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { label: '🔥 몰입', value: '몰입', color: '#1a237e' },
                { label: '💡 통찰', value: '통찰', color: '#283593' },
                { label: '😊 유쾌', value: '유쾌', color: '#FFD700' },
                { label: '😴 지루', value: '지루', color: '#6c757d' },
                { label: '😤 답답', value: '답답', color: '#495057' },
              ].map((item) => (
                <Box
                  key={item.value}
                  onClick={() => {
                    if (emotions.includes(item.value)) {
                      setEmotions(emotions.filter((v) => v !== item.value));
                    } else if (emotions.length < 3) {
                      setEmotions([...emotions, item.value]);
                    }
                    // 감정 선택 시 해당 섹션으로 스크롤
                    setTimeout(() => scrollToSection(emotionsRef), 100);
                  }}
                  sx={{
                    px: 1.6, py: 1,
                    borderRadius: 20,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    cursor: emotions.includes(item.value) ? 'pointer' : emotions.length < 3 ? 'pointer' : 'not-allowed',
                    boxShadow: emotions.includes(item.value) ? `0 3px 12px 0 ${item.color}30` : '0 1px 3px 0 rgba(0,0,0,0.1)',
                    bgcolor: emotions.includes(item.value) ? item.color : '#fff',
                    color: emotions.includes(item.value) ? '#fff' : '#666',
                    border: emotions.includes(item.value) ? `2px solid ${item.color}` : '1.5px solid #e0e0e0',
                    mr: 0.5,
                    mb: 0.5,
                    minWidth: 68,
                    textAlign: 'center',
                                      userSelect: 'none',
                    transition: 'all 0.2s ease',
                    opacity: !emotions.includes(item.value) && emotions.length >= 3 ? 0.4 : 1,
                  '&:hover': {
                      bgcolor: emotions.includes(item.value) ? item.color : emotions.length < 3 ? '#f8f9fa' : '#fff',
                      transform: emotions.length < 3 || emotions.includes(item.value) ? 'translateY(-2px)' : 'none',
                      boxShadow: emotions.includes(item.value) ? `0 5px 16px 0 ${item.color}40` : emotions.length < 3 ? '0 3px 8px 0 rgba(0,0,0,0.15)' : '0 1px 3px 0 rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* 소장 여부 섹션 */}
        <Box ref={genresRef} sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '1.05rem', color: '#333' }}>
            📚 소장 여부 <Typography component="span" sx={{ fontSize: '0.8rem', color: '#888', fontWeight: 400 }}>(최대 2개)</Typography>
          </Typography>
          <Box sx={{ 
            p: 2.5,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 3,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#f1f3f4',
              borderColor: '#dee2e6',
            }
          }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { label: '📦 소장 중', value: '소장중', color: '#1a237e' },
                { label: '💸 읽고 판매함', value: '읽고판매함', color: '#495057' },
                { label: '📖 빌려 읽음', value: '빌려읽음', color: '#283593' },
                { label: '🎁 선물받음', value: '선물받음', color: '#FFD700' },
                { label: '💰 구매 예정', value: '구매예정', color: '#333' },
              ].map((item) => (
                <Box
                  key={item.value}
                  onClick={() => {
                    if (genres.includes(item.value)) {
                      setGenres(genres.filter((v) => v !== item.value));
                    } else if (genres.length < 2) {
                      setGenres([...genres, item.value]);
                    }
                    // 소장 여부 선택 시 해당 섹션으로 스크롤
                    setTimeout(() => scrollToSection(genresRef), 100);
                  }}
                  sx={{
                    px: 1.6, py: 1,
                    borderRadius: 16,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    cursor: genres.includes(item.value) ? 'pointer' : genres.length < 2 ? 'pointer' : 'not-allowed',
                    boxShadow: genres.includes(item.value) ? `0 3px 12px 0 ${item.color}30` : '0 1px 3px 0 rgba(0,0,0,0.1)',
                    bgcolor: genres.includes(item.value) ? item.color : '#fff',
                    color: genres.includes(item.value) ? '#fff' : '#666',
                    border: genres.includes(item.value) ? `2px solid ${item.color}` : '1.5px solid #e0e0e0',
                    mr: 0.5,
                    mb: 0.5,
                    minWidth: 68,
                    textAlign: 'center',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    opacity: !genres.includes(item.value) && genres.length >= 2 ? 0.4 : 1,
                    '&:hover': {
                      bgcolor: genres.includes(item.value) ? item.color : genres.length < 2 ? '#f8f9fa' : '#fff',
                      transform: genres.length < 2 || genres.includes(item.value) ? 'translateY(-2px)' : 'none',
                      boxShadow: genres.includes(item.value) ? `0 5px 16px 0 ${item.color}40` : genres.length < 2 ? '0 3px 8px 0 rgba(0,0,0,0.15)' : '0 1px 3px 0 rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* 읽은 이유 섹션 */}
        <Box ref={mustReadRef} sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '1.05rem', color: '#333' }}>
            🎯 읽은 이유 <Typography component="span" sx={{ fontSize: '0.8rem', color: '#888', fontWeight: 400 }}>(최대 3개)</Typography>
          </Typography>
          <Box sx={{ 
            p: 2.5,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 3,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#f1f3f4',
              borderColor: '#dee2e6',
            }
          }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { label: '💪 성장', value: '성장', color: '#1a237e' },
                { label: '❤️ 힐링', value: '힐링', color: '#495057' },
                { label: '🌍 세계관 확장', value: '세계관확장', color: '#283593' },
                { label: '👁️ 견문 넓히기', value: '견문넓히기', color: '#6c757d' },
                { label: '💼 업무 도움', value: '업무도움', color: '#333' },
                { label: '🧠 지식 습득', value: '지식습득', color: '#1a237e' },
                { label: '🎭 재미/오락', value: '재미오락', color: '#FFD700' },
                { label: '🔍 호기심 해결', value: '호기심해결', color: '#283593' },
              ].map((item) => (
                <Box
                  key={item.value}
                  onClick={() => {
                    if (mustRead.includes && mustRead.includes(item.value)) {
                      const currentMeanings = typeof mustRead === 'string' ? (mustRead ? [mustRead] : []) : mustRead;
                      const newMeanings = currentMeanings.filter((v) => v !== item.value);
                      setMustRead(newMeanings);
                    } else {
                      const currentMeanings = typeof mustRead === 'string' ? (mustRead ? [mustRead] : []) : mustRead;
                      if (currentMeanings.length < 3) {
                        setMustRead([...currentMeanings, item.value]);
                      }
                    }
                    // 읽은 이유 선택 시 해당 섹션으로 스크롤
                    setTimeout(() => scrollToSection(mustReadRef), 100);
                  }}
                  sx={{
                    px: 1.6, py: 1,
                    borderRadius: 14,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    cursor: (() => {
                      const currentMeanings = typeof mustRead === 'string' ? (mustRead ? [mustRead] : []) : mustRead;
                      return currentMeanings.includes(item.value) ? 'pointer' : currentMeanings.length < 3 ? 'pointer' : 'not-allowed';
                    })(),
                    boxShadow: (() => {
                      const currentMeanings = typeof mustRead === 'string' ? (mustRead ? [mustRead] : []) : mustRead;
                      return currentMeanings.includes(item.value) ? `0 3px 12px 0 ${item.color}30` : '0 1px 3px 0 rgba(0,0,0,0.1)';
                    })(),
                    bgcolor: (() => {
                      const currentMeanings = typeof mustRead === 'string' ? (mustRead ? [mustRead] : []) : mustRead;
                      return currentMeanings.includes(item.value) ? item.color : '#fff';
                    })(),
                    color: (() => {
                      const currentMeanings = typeof mustRead === 'string' ? (mustRead ? [mustRead] : []) : mustRead;
                      return currentMeanings.includes(item.value) ? '#fff' : '#666';
                    })(),
                    border: (() => {
                      const currentMeanings = typeof mustRead === 'string' ? (mustRead ? [mustRead] : []) : mustRead;
                      return currentMeanings.includes(item.value) ? `2px solid ${item.color}` : '1.5px solid #e0e0e0';
                    })(),
                    mr: 0.5,
                    mb: 0.5,
                    minWidth: 68,
                    textAlign: 'center',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    opacity: (() => {
                      const currentMeanings = typeof mustRead === 'string' ? (mustRead ? [mustRead] : []) : mustRead;
                      return !currentMeanings.includes(item.value) && currentMeanings.length >= 3 ? 0.4 : 1;
                    })(),
                    '&:hover': {
                      bgcolor: (() => {
                        const currentMeanings = typeof mustRead === 'string' ? (mustRead ? [mustRead] : []) : mustRead;
                        if (currentMeanings.includes(item.value)) return item.color;
                        return currentMeanings.length < 3 ? '#f8f9fa' : '#fff';
                      })(),
                      transform: (() => {
                        const currentMeanings = typeof mustRead === 'string' ? (mustRead ? [mustRead] : []) : mustRead;
                        return currentMeanings.length < 3 || currentMeanings.includes(item.value) ? 'translateY(-2px)' : 'none';
                      })(),
                      boxShadow: (() => {
                        const currentMeanings = typeof mustRead === 'string' ? (mustRead ? [mustRead] : []) : mustRead;
                        if (currentMeanings.includes(item.value)) return `0 5px 16px 0 ${item.color}40`;
                        return currentMeanings.length < 3 ? '0 3px 8px 0 rgba(0,0,0,0.15)' : '0 1px 3px 0 rgba(0,0,0,0.1)';
                      })(),
                  },
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        
      </Box>
        {cropModalOpen && (
          <CropModal
            imageFile={pendingImageFile}
            onCropComplete={handleCropComplete}
            onClose={() => { setCropModalOpen(false); setPendingImageFile(null); }}
            aspectRatio={2/3}
            hideAspectRatioControls={true}
          />
        )}
        
      {/* 하단 고정 저장 버튼 */}
      <Box sx={{ 
        position: 'fixed', 
        left: 0, 
        right: 0, 
        bottom: 0, 
        bgcolor: 'rgba(255,255,255,0.95)', 
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(233,236,239,0.8)', 
        py: 2.5, 
        px: 2, 
        zIndex: 1200,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Button
            onClick={handleSave}
            fullWidth
            variant="contained"
            disabled={saving}
            sx={{ 
              bgcolor: '#1a237e', 
              color: '#fff', 
              fontWeight: 700, 
              py: 1.8, 
              fontSize: '1.1rem', 
              borderRadius: 3, 
              boxShadow: '0 4px 16px rgba(26,35,126,0.3)', 
              textTransform: 'none',
              transition: 'all 0.2s ease',
              '&:hover': { 
                bgcolor: '#283593',
                boxShadow: '0 6px 20px rgba(26,35,126,0.4)',
                transform: 'translateY(-1px)'
              },
              '&:active': {
                transform: 'translateY(0px)'
              },
              '&:disabled': {
                bgcolor: '#6c757d',
                boxShadow: 'none',
                transform: 'none'
              }
            }}
          >
            {saving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: '#fff' }} />
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>저장 중...</Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>저장하기</Typography>
            )}
          </Button>
        </Box>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default BookMemoEdit; 